import React from 'react';
import { ArrowLeft, ArrowRight, Boxes, CreditCard, Smartphone, Globe, BarChart3, UtensilsCrossed } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

// TODO: replace with bespoke Products hub hero artwork when supplied
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/vgn7acm2_PRODUCTS%20LANDING%20POAGE%20OPTION.png';

const products = [
  {
    id: 'pos-card-machines',
    icon: CreditCard,
    tagline: 'In-store payments',
    title: 'POS Card Machines',
    description: 'Professional payment terminals built for reliability and growth — our NEXGO range pairs Android flexibility with PCI-grade security.',
    accent: 'from-sky-600/15 to-blue-500/10',
  },
  {
    id: 'epos-and-mini',
    icon: UtensilsCrossed,
    tagline: 'Hospitality',
    title: 'EPOS & EPOS Mini',
    description: 'All-in-one ordering and payment systems designed for hospitality — from full service restaurants to busy quick service venues.',
    accent: 'from-amber-600/15 to-orange-500/10',
  },
  {
    id: 'transaction-reporting',
    icon: BarChart3,
    tagline: 'Insights & analytics',
    title: 'Transaction Reporting',
    description: 'Intelligent insights that transform raw transaction data into actionable business intelligence — realtime dashboards, deep-dive analytics and automated reporting.',
    accent: 'from-rose-600/15 to-pink-500/10',
  },
  {
    id: 'softpos-and-mpos',
    icon: Smartphone,
    tagline: 'On-the-go',
    title: 'SoftPOS & MPOS',
    description: 'Turn any Android device into a payment terminal or choose dedicated mobile hardware. Flexibility for businesses on the move.',
    accent: 'from-emerald-600/15 to-teal-500/10',
  },
  {
    id: 'ecommerce-solutions',
    icon: Globe,
    tagline: 'Online payments',
    title: 'eCommerce Solutions',
    description: 'Direct API control or pre-certified Hosted Payment Page. Flexible online payment options and features designed to suit businesses of all sizes.',
    accent: 'from-violet-600/15 to-purple-500/10',
  },
];

const Products = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="products-hub-page">
      <Header />
      <div className="h-20" />

      {/* Hero with full-page image */}
      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        {/* No overlay — image used as-is per user request */}

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-white font-medium mb-8 transition-colors duration-200"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.65)' }}
            data-testid="products-back-button"
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>

          <div className="mb-10 max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25] text-white text-[11px] font-bold uppercase tracking-[0.15em] mb-5 shadow-lg"
              data-testid="products-eyebrow"
            >
              <Boxes size={13} />
              The eCOMM range
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight"
              style={{ textShadow: '0 4px 20px rgba(0,0,0,0.55)' }}
              data-testid="products-title"
            >
              Products
            </h1>
            <p
              className="text-lg text-white font-bold leading-relaxed max-w-3xl"
              style={{ textShadow: '0 2px 14px rgba(0,0,0,0.6)' }}
            >
              Everything you need to take payments — in store, at the table, on the road and online. Our complete product range is built around your operation, with the right device or integration for every customer interaction.
            </p>
          </div>

          {/* 5 Product cards — 3-column grid with Transaction Reporting spanning 2 rows, centered on the side */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="products-grid">
            {products.map((p) => {
              const Icon = p.icon;
              const isSideCard = p.id === 'transaction-reporting';
              return (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className={`group relative overflow-hidden bg-white/15 backdrop-blur-md border border-white/25 rounded-lg shadow-2xl p-6 hover:bg-white/20 hover:border-white/40 hover:-translate-y-1 transition-all duration-300 ${
                    isSideCard ? 'mobile-last md:row-span-2 md:self-center md:h-fit' : ''
                  }`}
                  data-testid={`products-card-${p.id}`}
                >
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${p.accent.replace('/15', '').replace('/10', '')} bg-white/40`} />
                  <div className={`absolute -top-6 -right-6 w-28 h-28 rounded-full blur-2xl bg-gradient-to-br ${p.accent} opacity-50 pointer-events-none`} />

                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={18} className="text-white" />
                      <p className="text-[10px] font-bold text-white/80 uppercase tracking-[0.15em]">{p.tagline}</p>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-white/90 transition-colors" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>{p.title}</h3>
                    <p className="text-sm text-white/90 leading-relaxed mb-4" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>{p.description}</p>
                    <span className="inline-flex items-center gap-1 text-white font-semibold text-xs group-hover:gap-2 transition-all">
                      Explore
                      <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div
            className="mt-8 w-full sm:w-fit max-w-full rounded-lg bg-[#1F4E79] border border-[#1F4E79] px-4 py-3 shadow-xl"
            data-testid="products-tip"
          >
            <p className="text-[10.5px] text-white font-semibold leading-snug sm:whitespace-nowrap">
              Not Sure Which Product Fits?{' '}
              <Link
                to="/contact"
                className="text-white underline decoration-white/70 hover:decoration-white decoration-1 underline-offset-2 font-bold"
              >
                Talk to Our Team
              </Link>
              {' '}— We'll Match You with the Right Combination of Products and Integrations for Your Business.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Products;
